"use client";

import type { ComponentType, SVGProps } from "react";

import { cn } from "@/lib/utils";

import {
  CareersDeptIconAccountTree,
  CareersDeptIconAdsClick,
  CareersDeptIconDataTable,
  CareersDeptIconHomeWork,
  CareersDeptIconPalette,
  CareersDeptIconPeople,
  CareersDeptIconSell,
  CareersDeptIconStackedBar,
} from "./careers-dept-icons";

export type CareersDeptChipTone =
  | "commercial"
  | "hr"
  | "sales"
  | "data"
  | "engineering"
  | "marketing"
  | "design"
  | "operations";

type IconConfig = {
  shell: string;
  glyph: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconClassName?: string;
};

/** Figma careers dept chips (7253:48569–7253:48669). */
const ICON_TONE: Record<CareersDeptChipTone, IconConfig> = {
  commercial: {
    shell: "bg-[#f0f5ff] border-[#c6daff]",
    glyph: "text-[#c6daff]",
    Icon: CareersDeptIconHomeWork,
    iconClassName: "size-[11px]",
  },
  hr: {
    shell: "bg-[#ecf8f5] border-[#b6e9dc]",
    glyph: "text-[#b6e9dc]",
    Icon: CareersDeptIconPeople,
    iconClassName: "size-[9.6px]",
  },
  sales: {
    shell: "bg-[#fef2f2] border-[#ffcdb5]",
    glyph: "text-[#ffcdb5]",
    Icon: CareersDeptIconSell,
    iconClassName: "size-[13px]",
  },
  data: {
    shell: "bg-[#eafaee] border-[#abf0b7]",
    glyph: "text-[#a9edb5]",
    Icon: CareersDeptIconDataTable,
    iconClassName: "size-[11px]",
  },
  engineering: {
    shell: "bg-[#feebfd] border-[#ffc1fd]",
    glyph: "text-[#ffc1fd]",
    Icon: CareersDeptIconAccountTree,
    iconClassName: "size-[11px]",
  },
  marketing: {
    shell: "bg-[#fef2f2] border-[#ffcdb5]",
    glyph: "text-[#ffcdb5]",
    Icon: CareersDeptIconAdsClick,
    iconClassName: "size-[15px]",
  },
  design: {
    shell: "bg-[#ecf8f5] border-[#b6e9dc]",
    glyph: "text-[#b6e9dc]",
    Icon: CareersDeptIconPalette,
    iconClassName: "size-[10px]",
  },
  operations: {
    shell: "bg-[#eafaee] border-[#abf0b7]",
    glyph: "text-[#90e7a0]",
    Icon: CareersDeptIconStackedBar,
    iconClassName: "size-[15px] -scale-y-100 rotate-180",
  },
};

export function CareersDeptChip({
  label,
  tone,
  emphasis = "default",
  className,
}: {
  label: string;
  tone: CareersDeptChipTone;
  emphasis?: "default" | "muted" | "faint";
  className?: string;
}) {
  const icon = ICON_TONE[tone];
  const Icon = icon.Icon;

  return (
    <span
      className={cn(
        "bg-grey-0 inline-flex shrink-0 items-center gap-[7px] rounded-full py-[7px] pr-[13px] pl-[7px] shadow-[0px_4px_11px_rgba(0,0,0,0.06)]",
        className
      )}
    >
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full border",
          icon.shell
        )}
        aria-hidden
      >
        <Icon className={cn(icon.iconClassName, icon.glyph)} />
      </span>
      <span
        className={cn(
          "text-[13px] leading-[1.2] tracking-[-0.52px] whitespace-nowrap",
          emphasis === "default" && "text-grey-600",
          emphasis === "muted" && "text-grey-300",
          emphasis === "faint" && "text-grey-400"
        )}
      >
        {label}
      </span>
    </span>
  );
}
